import { type ReadonlyURLSearchParams, usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useUser from '@alltrails/context/hooks/useUser';
import { getValidDistanceAway } from '@alltrails/explore-service/hooks/filterConfigs/useDistanceAwayFilterConfig';
import { getValidTrailEngagements } from '@alltrails/explore-service/hooks/filterConfigs/useTrailEngagementFilterConfig';
import type { NaturalLanguageSearchAnalyticData } from '@alltrails/explore-service/redux/exploreServiceSlice';
import AdminExploreFilters, {
  AdminExploreFilterLocation,
  AdminExploreFilterLocationType
} from '@alltrails/explore-service/types/AdminExploreFilters';
import type DistanceAway from '@alltrails/explore-service/types/DistanceAway';
import type ExploreServiceSort from '@alltrails/explore-service/types/ExploreServiceSort';
import { defaultExploreServiceSort } from '@alltrails/explore-service/utils/constants';
import {
  getCurrentMonth,
  monthToApiParam,
  monthToSortParam,
  type SeasonalMonth,
  sortParamToMonth
} from '@alltrails/explore-service/utils/seasonalMonth';
import {
  activityParamKey,
  adminAreaParamKey,
  adminCityParamKey,
  adminClosedParamKey,
  adminCountryParamKey,
  adminStateParamKey,
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  createDistanceAwayParamString,
  difficultyParamKey,
  distanceAwayParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  latParamKey,
  lengthParamKey,
  lngParamKey,
  minRatingParamKey,
  naturalLanguageClickPositionParamKey,
  naturalLanguageQueryParamKey,
  naturalLanguageSearchOriginParamKey,
  naturalLanguageSelectionMethodParamKey,
  naturalLanguageSelectionTypeParamKey,
  parseDistanceAwayParamString,
  routeTypeParamKey,
  sortParamKey,
  suitabilityParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey,
  trailEngagementParamKey,
  trailTrafficParamKey
} from '@alltrails/filters/utils/exploreSearchParams';
import useNaturalLanguageSearchExperiment from '@alltrails/layout/hooks/useNaturalLanguageSearchExperiment';
import useMap from '@alltrails/maps/hooks/useMap';
import { parseFiltersFromSearchParams, serializeFiltersToSearchParams } from '@/utils/exploreFilterParams';
import getPlace from '@/utils/getPlace';
import { useAppSelector } from '@/utils/redux';
import replaceSearchParams from '@/utils/replaceSearchParams';

export type ExploreParamCategories = 'boundingBox' | 'distanceAway' | 'filters' | 'naturalLanguageQuery';

const locationTypeParamKeys: Record<AdminExploreFilterLocationType, string> = {
  area: adminAreaParamKey,
  city: adminCityParamKey,
  state: adminStateParamKey,
  country: adminCountryParamKey
};

export const getParamValue = (searchParams: ReadonlyURLSearchParams, key: string): string | null => {
  const value = searchParams.get(key);
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

const getParamValues = (searchParams: ReadonlyURLSearchParams, key: string): string[] => {
  const value = searchParams.getAll(key);
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }
  return [value];
};

const exploreControlledParamKeys = [
  activityParamKey,
  adminAreaParamKey,
  adminCityParamKey,
  adminClosedParamKey,
  adminCountryParamKey,
  adminStateParamKey,
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  difficultyParamKey,
  distanceAwayParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  latParamKey,
  lengthParamKey,
  lngParamKey,
  minRatingParamKey,
  naturalLanguageQueryParamKey,
  naturalLanguageClickPositionParamKey,
  naturalLanguageSelectionMethodParamKey,
  naturalLanguageSelectionTypeParamKey,
  routeTypeParamKey,
  sortParamKey,
  suitabilityParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey,
  trailEngagementParamKey,
  trailTrafficParamKey
];

const useExploreSearchParams = (paramsToInclude?: ExploreParamCategories[]) => {
  const map = useMap();
  const pathname = usePathname();
  const user = useUser();
  const { hasPermission } = useAuthorization();
  const searchParams = useSearchParams();
  const { isNaturalLanguageSearchEnabled } = useNaturalLanguageSearchExperiment();

  const {
    adminFilters,
    boundingBox,
    distanceAway,
    exploreServiceFilters,
    exploreServiceSort,
    naturalLanguageQuery,
    seasonalMonth,
    trailEngagement,
    useMetric
  } = useAppSelector(state => ({
    adminFilters: state.exploreService.adminFilters,
    boundingBox: state.exploreService.boundingBox,
    distanceAway: state.exploreService.distanceAway,
    exploreServiceFilters: state.exploreService.filters,
    exploreServiceSort: state.exploreService.sort,
    naturalLanguageQuery: state.exploreService.naturalLanguageQuery,
    seasonalMonth: state.exploreService.seasonalMonth,
    trailEngagement: state.exploreService.trailEngagement,
    useMetric: state.context.displayMetric
  }));

  useEffect(() => {
    if (!map) {
      // wait until the map is loaded before setting params to avoid conflict with initial load
      return;
    }

    // Rather than trying to figure out what filters have changed when an update occurs,
    // clear and recalculate the new explore-controlled search params.
    // Preserve params that are not controlled by the explore app.
    const currentParams = new URLSearchParams(window.location.search);
    const newSearchParams = new URLSearchParams(window.location.search);
    exploreControlledParamKeys.forEach(key => {
      newSearchParams.delete(key);
    });
    const includeBoundingBox = paramsToInclude?.includes('boundingBox') ?? true;
    const includeDistanceAway = paramsToInclude?.includes('distanceAway') ?? true;
    const includeFilters = paramsToInclude?.includes('filters') ?? true;
    const includeNaturalLanguageQuery = paramsToInclude?.includes('naturalLanguageQuery') ?? true;

    if (boundingBox && includeBoundingBox) {
      newSearchParams.append(bottomRightLatParamKey, String(boundingBox.bottomRight.lat));
      newSearchParams.append(bottomRightLngParamKey, String(boundingBox.bottomRight.lng));
      newSearchParams.append(topLeftLatParamKey, String(boundingBox.topLeft.lat));
      newSearchParams.append(topLeftLngParamKey, String(boundingBox.topLeft.lng));
    }

    if (distanceAway && includeDistanceAway) {
      newSearchParams.append(distanceAwayParamKey, createDistanceAwayParamString(distanceAway));
    }

    if (includeFilters) {
      serializeFiltersToSearchParams(exploreServiceFilters, newSearchParams);
      trailEngagement?.forEach(engagement => {
        newSearchParams.append(trailEngagementParamKey, engagement);
      });
    }

    if (naturalLanguageQuery && includeNaturalLanguageQuery) {
      newSearchParams.append(naturalLanguageQueryParamKey, naturalLanguageQuery);
    }

    if (exploreServiceSort && exploreServiceSort !== defaultExploreServiceSort && includeFilters) {
      // Never write 'seasonal' to URL - always use 'month_X' format for consistency
      const sortParamValue = exploreServiceSort === 'seasonal' ? monthToSortParam(seasonalMonth || getCurrentMonth()) : exploreServiceSort;
      newSearchParams.append(sortParamKey, sortParamValue);
    }

    if (hasPermission({ permission: 'trails:manage' }) && adminFilters) {
      if (adminFilters.isClosed !== undefined) {
        newSearchParams.append(adminClosedParamKey, String(adminFilters.isClosed));
      }

      (Object.keys(locationTypeParamKeys) as AdminExploreFilterLocationType[]).forEach(locationType => {
        if (adminFilters[locationType]?.length) {
          const paramKey = locationTypeParamKeys[locationType];
          adminFilters[locationType].forEach(location => {
            newSearchParams.append(paramKey, String(location.id));
          });
        }
      });
    }

    if (newSearchParams.toString() === currentParams.toString()) {
      return;
    }

    replaceSearchParams(pathname, newSearchParams);
  }, [
    adminFilters,
    boundingBox,
    distanceAway,
    exploreServiceFilters,
    exploreServiceSort,
    hasPermission,
    map,
    naturalLanguageQuery,
    paramsToInclude,
    pathname,
    searchParams,
    seasonalMonth,
    trailEngagement
  ]);

  const initialTrailEngagement = useMemo(() => {
    if (!user) {
      return [];
    }
    return getValidTrailEngagements(getParamValues(searchParams, trailEngagementParamKey));
    // We only want to run this on the initial load
  }, []);

  const initialDistanceAway = useMemo<DistanceAway | undefined>(() => {
    const distanceAwayParamValue = getParamValue(searchParams, distanceAwayParamKey);
    if (!distanceAwayParamValue) {
      return undefined;
    }
    const { center, distance } = parseDistanceAwayParamString(distanceAwayParamValue);
    const validDistance = getValidDistanceAway(distance, useMetric);

    if (!validDistance) {
      return undefined;
    }

    return { center, distance: validDistance };
    // We only want to run this on the initial load
  }, []);

  const initialNaturalLanguageSearchData = useMemo<{ analyticsData?: NaturalLanguageSearchAnalyticData; query?: string } | undefined>(() => {
    if (!isNaturalLanguageSearchEnabled) {
      return undefined;
    }
    const query = getParamValue(searchParams, naturalLanguageQueryParamKey);
    if (!query) {
      return undefined;
    }

    const clickPositionParamValue = getParamValue(searchParams, naturalLanguageClickPositionParamKey);
    const selectionMethodParamValue = getParamValue(searchParams, naturalLanguageSelectionMethodParamKey);
    const selectionTypeParamValue = getParamValue(searchParams, naturalLanguageSelectionTypeParamKey);
    const searchOriginParamValue = getParamValue(searchParams, naturalLanguageSearchOriginParamKey);

    const analyticsData: NaturalLanguageSearchAnalyticData = {
      clickPosition: clickPositionParamValue ? Number(clickPositionParamValue) : undefined,
      queryText: query,
      searchOrigin: searchOriginParamValue as any,
      selectionType: selectionTypeParamValue as any,
      selectionMethod: selectionMethodParamValue as any
    };

    return { analyticsData, query };

    // We only want to run this on the initial load
  }, []);

  const initialSeasonalMonth = useMemo<SeasonalMonth | undefined>(() => {
    const sortParam = getParamValue(searchParams, sortParamKey);

    // Only set seasonal month if there's a seasonal sort parameter
    // This ensures seasonalMonth is only set when explicitly using seasonal sorting
    if (sortParam === 'seasonal' || sortParam?.startsWith('month_')) {
      return sortParamToMonth(sortParam);
    }

    // Return undefined instead of current month when no seasonal sort is active
    // This prevents overriding the default state with an explicit undefined value
    return undefined;
    // We only want to run this on the initial load
  }, []);

  const initialSort = useMemo<ExploreServiceSort | undefined>(() => {
    const sortParam = getParamValue(searchParams, sortParamKey);
    if (sortParam) {
      // If URL has month_X format, convert to 'seasonal' if X is current month
      // This ensures API receives 'seasonal' for current month
      if (sortParam.startsWith('month_')) {
        const month = sortParamToMonth(sortParam);
        return monthToApiParam(month) as ExploreServiceSort;
      }
      return sortParam as ExploreServiceSort;
    }
    return undefined;
    // We only want to run this on the initial load
  }, []);

  const initialFilters = useMemo(() => parseFiltersFromSearchParams(searchParams, useMetric), []);

  const [initialAdminFilters, setInitialAdminFilters] = useState<AdminExploreFilters>({});
  useEffect(() => {
    if (!hasPermission({ permission: 'trails:manage' })) {
      return;
    }

    try {
      const fetchPlaces = async () => {
        const filters: AdminExploreFilters = {};

        const closed = getParamValue(searchParams, adminClosedParamKey);
        if (closed === 'true') {
          filters.isClosed = true;
        } else if (closed === 'false') {
          filters.isClosed = false;
        }

        for (const locationType of Object.keys(locationTypeParamKeys) as AdminExploreFilterLocationType[]) {
          const values = getParamValues(searchParams, locationTypeParamKeys[locationType]);
          if (values.length) {
            const locations: AdminExploreFilterLocation<typeof locationType>[] = [];
            for (const idString of values) {
              const { place } = await getPlace({ id: Number(idString), placeType: locationType });
              if (place) {
                locations.push({ id: place.ID, name: place.name, type: locationType });
              }
            }

            filters[locationType] = locations;
          }
        }
        setInitialAdminFilters(filters);
      };
      fetchPlaces();
    } catch (error) {
      // Don't need crazy error handling for admins but want to ensure that this doesn't break the whole page
      console.log(error);
    }
    // We only want to run this on the initial load
  }, []);

  return useMemo(
    () => ({
      initialAdminFilters,
      initialDistanceAway,
      initialFilters,
      initialNaturalLanguageSearchData,
      initialSeasonalMonth,
      initialSort,
      initialTrailEngagement
    }),
    [
      initialAdminFilters,
      initialDistanceAway,
      initialFilters,
      initialNaturalLanguageSearchData,
      initialSeasonalMonth,
      initialSort,
      initialTrailEngagement
    ]
  );
};

export default useExploreSearchParams;
